import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { tsr } from '../../lib/api.js';

/**
 * A persistent notice, on every page, when the organisation's subscription payment is overdue.
 *
 * Mounted by AppShell only for someone who can read billing (`org.billing:read`), because
 * `getSubscription` is gated on it: an ordinary employee neither sees the banner nor triggers the
 * request. `past_due` is set by the daily dunning sweep (apps/worker/src/workers/billing-dunning.ts)
 * once an invoice has been unpaid for 15 days, and cleared by the payment confirmation. Hidden on
 * the billing pages themselves, which already show the same state in context.
 */
export function BillingOverdueBanner() {
  const { pathname } = useLocation();
  // A 404 is an answer — the org has never checked out — not a failure. Through the ts-rest hook
  // it became an error state, which is never cached as fresh, so every new mount asked again.
  // Fetched through `.query()` (which does not throw on a non-2xx) so it caches as `null`.
  // Own key under ['billing', 'subscription']: the billing pages cache the raw response there, a
  // different shape, and their invalidations still reach this one by prefix.
  const query = useQuery({
    queryKey: ['billing', 'subscription', 'overdue-banner'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await tsr.billing.getSubscription.query();
      if (res.status === 200) return res.body;
      if (res.status === 404) return null;
      throw res;
    },
  });

  const overdue = query.data?.status === 'past_due';
  if (!overdue || pathname.startsWith('/admin/billing')) return null;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-danger bg-danger-subtle px-4 py-2.5 text-sm text-danger sm:px-6"
    >
      <span>
        Your subscription payment is overdue. Please settle the pending invoice to avoid account
        suspension.
      </span>
      <Link to="/admin/billing/invoices" className="font-semibold underline">
        View invoice
      </Link>
    </div>
  );
}
